<template>
  <div class="multi-step-quiz-v2">
    <!--- Questions list -->
    <template v-for="(question, questionIndex) in questions">
      <div class="quiz-question" :key="`question-${questionIndex}`">
        <div class="quiz-item">
          <div class="quiz-item__header">
            <span class="quiz-item__header-title">Question {{ questionIndex + 1 }}</span>
            <span class="quiz-item__remove ml-auto" @click="removeQuestion(questionIndex)">
              {{ $t("shortcodes.multiStepQuiz.delete") }}
            </span>
          </div>
          <va-tinymce-input
            class="w-full"
            :current-content="question.content"
            :editor-height="300"
            stripped-down
            @update="content => updateQuestionContent(question, content)" />
        </div>
        <!--- Answers list -->
        <div class="quiz-question__answers-wrapper">
          <template v-for="(answer, answerIndex) in question.answers">
            <div
              :class="[
                answer.destination === 'success' ? 'quiz-question__answer--success' : '',
                answer.destination === 'fail' ? 'quiz-question__answer--fail' : '',
              ]"
              class="quiz-question__answer d-flex align-center"
              :key="`answer-${answerIndex}`">
              <v-text-field
                v-model="questions[questionIndex].answers[answerIndex].content"
                :label="`Answer ${answerIndex + 1}`"></v-text-field>

              <v-tooltip top>
                <template #activator="{ on }">
                  <v-icon
                    @click="toggleSuccessAnswer(answer)"
                    v-on="on"
                    class="quiz-question__answers-icon"
                    color="icon"
                    :class="[answer.destination === 'success' ? 'quiz-question__answers-icon-active--success' : '']">
                    mdi-check</v-icon
                  >
                </template>
                <p>Add destination - Success</p>
              </v-tooltip>

              <v-tooltip top>
                <template #activator="{ on }">
                  <v-icon
                    @click="toggleFailAnswer(answer)"
                    v-on="on"
                    class="quiz-question__answers-icon"
                    color="icon"
                    :class="[answer.destination === 'fail' ? 'quiz-question__answers-icon-active--fail' : '']">
                    mdi-close</v-icon
                  >
                </template>
                <p>Add destination - Fail</p>
              </v-tooltip>

              <v-tooltip top>
                <template #activator="{ on }">
                  <v-icon
                    @click="removeAnswer(question, answerIndex)"
                    v-on="on"
                    class="quiz-question__answers-icon--delete"
                    color="icon">
                    mdi-delete-forever</v-icon
                  >
                </template>
                <p>Remove answer</p>
              </v-tooltip>
            </div>
          </template>
          <button class="multi-step-quiz-v2__button-answer" @click="addAnswer(question)">
            <plus-icon class="fill--primary mr-2"></plus-icon> Add Answer
          </button>
        </div>
      </div>
    </template>
    <!--- Add Question btn -->
    <button class="multi-step-quiz-v2__button-question" :disabled="addQuestionDisabled" @click="addQuestion">
      <plus-icon class="fill--primary mr-2"></plus-icon>
      Add Question
    </button>
    <!---Destinations-->
    <div class="destinations__wrapper">
      <div class="quiz-item quiz-item--success">
        <div class="quiz-item__header">
          <span class="quiz-item__header-title">Success Destination </span>
        </div>
        <va-tinymce-input
          class="w-full"
          :current-content="destinations.success.content"
          :editor-height="300"
          stripped-down
          @update="content => updateDestinationContent(destinations.success, content)" />

        <code-input v-model="destinations.success.pixelCode" :title="'Pixel code'"></code-input>
      </div>

      <div class="quiz-item quiz-item--fail">
        <div class="quiz-item__header">
          <span class="quiz-item__header-title">Fail Destination</span>
        </div>
        <va-tinymce-input
          class="w-full"
          :current-content="destinations.fail.content"
          :editor-height="300"
          stripped-down
          @update="content => updateDestinationContent(destinations.fail, content)" />

        <v-checkbox
          class="display-reset-checkbox"
          :label="'Display Reset Button'"
          v-model="destinations.fail.displayResetButton"
          color="primary">
        </v-checkbox>
      </div>
    </div>

    <!---JSON code-->
    <div class="quiz-item quiz-item" :class="[jsonFormatError ? 'json-error' : '']">
      <div class="quiz-item__header">
        <span class="quiz-item__header-title">JSON </span>
        <info-tooltip :info="'You can edit questions and destinations by editing the JSON below'"></info-tooltip>

        <v-tooltip v-if="jsonFormatError" top>
          <template #activator="{ on }">
            <v-icon v-on="on" @click="undoJson" class="ml-auto">mdi-undo</v-icon>
          </template>
          <p>Undo to previous valid JSON state</p>
        </v-tooltip>
      </div>
      <code-input :key="jsonInputKey" v-model="questionsJsonFormat" :title="''"></code-input>
      <p class="quiz-error">{{ jsonFormatError ? "Wrong JSON format" : "" }}</p>
    </div>
  </div>
</template>

<script>
  // Icons
  import PlusIcon from "@/components/icons/PlusIcon";
  // Helpers
  import cloneDeep from "lodash/cloneDeep";
  import find from "lodash/find";
  // Components
  import CodeInput from "@/components/custom/CodeInput";
  import InfoTooltip from "@/components/custom/InfoTooltip";
  // Utils
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
  import { convertAssetsPathToRelativeLinks, convertAssetsPathToAbsoluteLinks } from "@/utility/convertAssetsPaths";
  // TODO: update with appropriate values
  const DEFAULT_QUESTION = {
    content: "Placeholder question",
    answers: [
      {
        content: "Yes",
        destination: null,
      },
      {
        content: "No",
        destination: null,
      },
    ],
  };

  export default {
    components: {
      PlusIcon,
      CodeInput,
      InfoTooltip,
    },
    data() {
      return {
        questions: [cloneDeep(DEFAULT_QUESTION)],
        destinations: {
          fail: {
            content: "",
            displayResetButton: false,
          },
          success: {
            content: "",
            pixelCode: "",
          },
        },
        /**
         * When editing JSON input (code-input), user might make the JSON inside of the code-input invalid;
         * jsonFormatError is true if JSON format inside of the code-input is incorrect (not parseable using JSON.parse)
         */
        jsonFormatError: false,
        paramData: null,
        jsonInputKey: 0,
      };
    },
    computed: {
      questionsJsonFormat: {
        get() {
          const displayData = { questions: this.questions, destinations: this.destinations };

          return JSON.stringify(displayData, null, " ");
        },
        set(value) {
          let parsedJson;
          try {
            this.jsonFormatError = false;
            parsedJson = JSON.parse(value);
          } catch (e) {
            this.jsonFormatError = true;
          }

          if (!this.jsonFormatError) {
            this.questions = parsedJson.questions;
            this.destinations = parsedJson.destinations;
          }
        },
      },
      /**
       * Data that will be emitted via postMessage
       */
      emitData() {
        return {
          questions: this.questions,
          destinations: this.destinations,
        };
      },
      addQuestionDisabled() {
        return !!find(this.questions, question => {
          return question.answers.every(answer => answer.destination);
        });
      },
    },
    watch: {
      questions: {
        deep: true,
        handler() {
          this.updateValue(this.emitData);
        },
      },
      destinations: {
        deep: true,
        handler() {
          this.updateValue(this.emitData);
        },
      },
    },
    beforeMount() {
      // Mounts a postmessage listener to window. Waits for "sendData" message from /multiStepQuizV2Config.
      this.setupReceiveDataListener();
      // As soon as receiveDataListener is ready, we can request data from /multiStepQuizV2Config.
      this.requestData();

      return;
    },
    methods: {
      /**
       * Updates the JSON input to previous valid state.
       */
      undoJson() {
        const displayData = {
          questions: this.questions,
          destinations: this.destinations,
        };
        this.questionsJsonFormat = JSON.stringify(displayData, null, " ");
        this.jsonInputKey++;
      },
      toggleSuccessAnswer(answer) {
        const removeSuccessDestination = answer.destination === "success";
        answer.destination = removeSuccessDestination ? null : "success";
      },
      toggleFailAnswer(answer) {
        const removeFailDestination = answer.destination === "fail";
        answer.destination = removeFailDestination ? null : "fail";
      },
      updateValue(val) {
        const stringifiedData = JSON.stringify(val, (key, value) => {
          if (key === "content") {
            // Replacing &emsp; with space, cause innerHTML doesn't deserialize from space to $emsp;
            value = value.replace(/&emsp;/g, " ");
            let parser = new DOMParser();
            // Creating document from string to run dynamic component serialization which uses DOM manipulation
            let document = parser.parseFromString(value, "text/html");
            serializeDynamicComponents(document);
            // Selecting only the body content of the document as the wrappers are not needed
            document = document.querySelector("body").innerHTML;
            // Converting assets paths to relative links
            document = convertAssetsPathToRelativeLinks(document)?.querySelector("body")?.innerHTML;

            return window.btoa(document);
          }

          return value;
        });

        window.parent.postMessage(
          {
            mceAction: "updateData",
            data: stringifiedData,
          },
          origin,
        );
      },
      updateQuestionContent(question, content) {
        question.content = content;
      },
      updateDestinationContent(destination, content) {
        destination.content = content;
      },
      addQuestion() {
        // CloneDeep to discard the reference between "answers" inner object of Question object.
        // Without this, editing a specific answer would edit all the answers (because reference would be kept).
        const newQuestion = cloneDeep(DEFAULT_QUESTION);
        this.questions.push(newQuestion);
      },
      addAnswer(question) {
        question.answers.push({
          content: "",
          destination: null,
        });
      },
      removeQuestion(questionIndex) {
        this.questions.splice(questionIndex, 1);
      },
      removeAnswer(question, answerIndex) {
        question.answers.splice(answerIndex, 1);
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (message.data.type === "sendData") {
            // Format and set the received data
            const { data } = message.data;
            const jsonData = JSON.parse(data, (key, value) => {
              if (key === "content") {
                // Converting assets paths to Absolute links
                return convertAssetsPathToAbsoluteLinks(atob(value));
              }

              return value;
            });

            this.questions = jsonData?.questions;
            this.destinations = jsonData?.destinations;
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../@core/preset/preset/typography/index.scss";
  @import "../@core/preset/preset/functions.scss";

  .multi-step-quiz-v2 {
    background: white;
    height: calc(100% + 32px);
    width: calc(100% + 24px);
    margin-left: -12px;
    margin-top: -12px;
    padding-top: 24px;
    padding-left: 24px;
    padding-right: 24px;

    &__button {
      &-question {
        width: 100%;
        text-align: center;
        background-color: get-color("primary-10-hover");
        border: 1px dashed get-color("primary-65");
        border-radius: 6px;
        padding: 8px;
        @include text-button-medium();
        color: get-color("primary");
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &-answer {
        margin-left: auto;
        margin-top: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        @include text-button-medium();
        color: get-color("primary");
      }
    }
  }

  .quiz-question {
    padding: 12px;
    border: 1px dashed get-color("primary");
    background: get-color("primary-10-hover");
    margin-bottom: 24px;
    // Answers
    &__answers {
      &-title {
        color: get-color("subtitle");
        @include heading-4();
        margin: 7px 0px;
      }
      &-wrapper {
        display: flex;
        flex-direction: column;
        padding: 0 10px 0 20px;
      }
      &-icon {
        height: 24px;
        width: 24px;
        border-radius: 50%;
        margin: 0 8px;
        cursor: pointer;
        &::after {
          content: none !important;
        }
        &--delete {
          &:hover {
            color: get-color("error") !important;
          }
        }
        &-active {
          &--success {
            &.icon--text {
              color: get-color("success") !important;
            }
          }
          &--fail {
            &.icon--text {
              color: get-color("error") !important;
            }
          }
        }
      }
    }
    &__answer {
      position: relative;
      padding: 5px 10px;
      margin: 10px 0;
      border: 1px dashed get-color("primary");
      border-radius: 5px;
      background: get-color("element-bg-100");
      &--success {
        border: 2px dashed get-color("success");
      }
      &--fail {
        border: 2px dashed get-color("error");
      }
      &::before {
        content: " ";
        position: absolute;
        height: 110px;
        left: -10px;
        top: 0;
        border: 1px dashed get-color("primary");
        border-left: none;
      }
      &::after {
        content: " ";
        position: absolute;
        height: 1px;
        width: 10px;
        left: -10px;
        top: 50%;
        transform: translateY(-50%);
        border: 1px dashed get-color("primary");
        border-bottom: none;
        border-left: none;
        border-top-right-radius: 10px;
      }
      &:first-of-type {
        &::before {
          top: -10px;
          height: 120px;
        }
      }
      &:last-of-type {
        &::before {
          top: -10px;
          height: 55px;
          border-bottom-right-radius: 10px;
        }
      }
    }
  }

  .quiz-item {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    padding: 2px;
    &--success {
      background-color: get-color("primary-10-hover");
      border: 1px dashed get-color("success");
      .va-code-input {
        margin-top: 10px;
      }
    }
    &--fail {
      background-color: get-color("primary-10-hover");
      border: 1px dashed get-color("error");
    }

    &__header {
      display: flex;
      padding: 5px;
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
      &-title {
        color: get-color("subtitle");
        @include text-caption-bold();
      }
    }

    &__remove {
      cursor: pointer;
      color: get-color("error");
      @include text-caption();
      &::after {
        content: none !important;
      }
    }

    .display-reset-checkbox {
      margin-top: 16px;
      &::v-deep {
        label {
          color: get-color("subtitle");
          @include text-body-1-bold();
        }
      }
    }
  }

  .destinations__wrapper {
    .quiz-item {
      margin: 10px 0;
    }
  }

  ::v-deep {
    .tox-tinymce {
      border: none !important;
    }
    .va-tinymce-input .va-tinymce-input__label-wrapper {
      margin-bottom: 0;
    }
  }

  .json-error {
    border: 1px solid get-color("error");
    border-radius: 5px;
    padding: 5px;
    &::v-deep {
      .va-code-input__label {
        color: get-color("error");
      }
    }
  }
  .quiz-error {
    @include text-caption();
    color: get-color("error");
    margin-top: 5px;
  }
</style>
